import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { useNavigate } from 'react-router-dom';
import { ApiUnavailableBanner } from '../components/ApiUnavailableBanner';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { PasswordRulesChecklist } from '../components/auth/PasswordRules';
import { PlatformTileGrid } from '../components/auth/PlatformTileGrid';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectProfile,
  selectProfileFetchStatus,
  selectProfileUpsertStatus,
} from '../store/selectors/profile-selectors';
import { fetchProfile, upsertProfileThunk } from '../store/thunks/profile-thunks';
import type { ProfileResponse } from '../lib/api/profile-api-client';
import { getCognitoConfig } from '../lib/auth/cognito-config';
import { changePassword } from '../lib/auth/email-password';
import {
  dateOfBirthError,
  firstNameError,
  lastNameError,
  normalizePhoneE164,
  passwordConfirmError,
  passwordError,
  phoneError,
  usernameError,
} from '../lib/auth/validation';

/**
 * Profile surface (round-22 sign-up UX rework — fully editable).
 *
 * On mount the page dispatches `fetchProfile()`. When the fetch
 * flips to `'not-found'` (first-time-visitor branch) the page
 * navigates to `/sign-up`. Otherwise the page renders the same
 * shape as `<SignUpPage>` — username, first/last, phone with country
 * selector, date of birth, preferred-platform tile grid, opt-ins —
 * but with the current profile row seeded into the form and with
 * every field editable.
 *
 * Email is displayed read-only (Cognito owns the email; changing it
 * requires re-verification which is a follow-up feature). A separate
 * password-change section at the bottom uses the Cognito SDK's
 * `changePassword` — the user supplies the current and new
 * passwords and the SDK swaps them out without leaving the page.
 */

interface FormState {
  readonly username: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phoneE164: string;
  readonly dateOfBirth: string;
  readonly preferredPlatforms: ReadonlyArray<string>;
  readonly optInNewsletter: boolean;
  readonly optInPaidFeatures: boolean;
}

function seedForm(profile: ProfileResponse): FormState {
  return {
    username: profile.username,
    firstName: profile.firstName,
    lastName: profile.lastName,
    phoneE164: profile.phoneE164 ?? '',
    dateOfBirth: profile.dateOfBirth ?? '',
    preferredPlatforms: profile.preferredPlatforms ?? [],
    optInNewsletter: profile.optInNewsletter,
    optInPaidFeatures: profile.optInPaidFeatures,
  };
}

interface TouchedState {
  username: boolean;
  firstName: boolean;
  lastName: boolean;
  phoneE164: boolean;
  dateOfBirth: boolean;
}

const INITIAL_TOUCHED: TouchedState = {
  username: false,
  firstName: false,
  lastName: false,
  phoneE164: false,
  dateOfBirth: false,
};

function ProfileLoading(): JSX.Element {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', p: 4 }}>
      <CircularProgress size={24} />
      <Typography>Loading profile…</Typography>
    </Stack>
  );
}

export default function ProfilePage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const profile = useAppSelector(selectProfile);
  const fetchStatus = useAppSelector(selectProfileFetchStatus);
  const upsertStatus = useAppSelector(selectProfileUpsertStatus);
  const upsertError = useAppSelector((state) => state.profile.upsertError);
  const [form, setForm] = useState<FormState | undefined>(undefined);
  const [touched, setTouched] = useState<TouchedState>(INITIAL_TOUCHED);
  const [savedSnackOpen, setSavedSnackOpen] = useState<boolean>(false);

  useEffect(() => {
    void dispatch(fetchProfile());
  }, [dispatch]);

  useEffect(() => {
    if (fetchStatus === 'not-found') navigate('/sign-up');
  }, [fetchStatus, navigate]);

  useEffect(() => {
    if (profile !== undefined && form === undefined) {
      setForm(seedForm(profile));
    }
  }, [profile, form]);

  useEffect(() => {
    if (upsertStatus === 'succeeded') setSavedSnackOpen(true);
  }, [upsertStatus]);

  const patch = (next: Partial<FormState>): void => {
    if (form === undefined) return;
    setForm({ ...form, ...next });
  };
  const touch = (field: keyof TouchedState): void =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (form === undefined) return;
    setTouched({
      username: true,
      firstName: true,
      lastName: true,
      phoneE164: true,
      dateOfBirth: true,
    });
    const err =
      usernameError(form.username) ??
      firstNameError(form.firstName) ??
      lastNameError(form.lastName) ??
      phoneError(form.phoneE164) ??
      dateOfBirthError(form.dateOfBirth);
    if (err !== undefined) return;
    void dispatch(
      upsertProfileThunk({
        username: form.username,
        firstName: form.firstName,
        lastName: form.lastName,
        phoneE164:
          form.phoneE164.length > 0 ? normalizePhoneE164(form.phoneE164) : undefined,
        dateOfBirth: form.dateOfBirth.length > 0 ? form.dateOfBirth : undefined,
        preferredPlatforms:
          form.preferredPlatforms.length > 0 ? form.preferredPlatforms : undefined,
        optInNewsletter: form.optInNewsletter,
        optInPaidFeatures: form.optInPaidFeatures,
      }),
    );
  }

  return (
    <Card sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/profile"
          pageTitle="Your profile"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 2 }}>
          Your profile
        </Typography>
        {renderBody({
          fetchStatus,
          profile,
          form,
          touched,
          upsertStatus,
          upsertError,
          onFormChange: patch,
          onTouch: touch,
          onSubmit: handleSubmit,
        })}
        <Snackbar
          open={savedSnackOpen}
          autoHideDuration={4000}
          onClose={() => setSavedSnackOpen(false)}
          message="Profile saved"
        />
      </CardContent>
    </Card>
  );
}

interface BodyArgs {
  readonly fetchStatus: ReturnType<typeof selectProfileFetchStatus>;
  readonly profile: ReturnType<typeof selectProfile>;
  readonly form: FormState | undefined;
  readonly touched: TouchedState;
  readonly upsertStatus: ReturnType<typeof selectProfileUpsertStatus>;
  readonly upsertError: string | undefined;
  readonly onFormChange: (next: Partial<FormState>) => void;
  readonly onTouch: (field: keyof TouchedState) => void;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

function renderBody(args: BodyArgs): JSX.Element {
  if (args.profile === undefined && args.fetchStatus === 'loading') {
    return <ProfileLoading />;
  }
  if (args.fetchStatus === 'failed') return <ApiUnavailableBanner />;
  if (args.profile === undefined || args.form === undefined) {
    return <ProfileLoading />;
  }
  return (
    <Stack spacing={3}>
      <ProfileForm {...args} form={args.form!} profile={args.profile!} />
      <Divider />
      <ChangePasswordSection email={args.profile!.email} />
    </Stack>
  );
}

interface FormProps {
  readonly form: FormState;
  readonly profile: ProfileResponse;
  readonly touched: TouchedState;
  readonly upsertStatus: ReturnType<typeof selectProfileUpsertStatus>;
  readonly upsertError: string | undefined;
  readonly onFormChange: (next: Partial<FormState>) => void;
  readonly onTouch: (field: keyof TouchedState) => void;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

function ProfileForm(props: FormProps): JSX.Element {
  const { form, profile, touched, onFormChange, onTouch, onSubmit } = props;
  const errUsername = usernameError(form.username);
  const errFirst = firstNameError(form.firstName);
  const errLast = lastNameError(form.lastName);
  const errPhone = phoneError(form.phoneE164);
  const errDob = dateOfBirthError(form.dateOfBirth);
  const saving = props.upsertStatus === 'loading';
  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        {!profile.emailConfirmed && (
          <Alert severity="warning" role="alert">
            Your email is not verified. Check your inbox for a Cognito
            verification message.
          </Alert>
        )}
        <TextField label="Email" value={profile.email} disabled />
        <TextField
          label="Username / gamertag"
          required
          value={form.username}
          onChange={(e) => onFormChange({ username: e.target.value })}
          onBlur={() => onTouch('username')}
          error={touched.username && errUsername !== undefined}
          helperText={
            touched.username && errUsername !== undefined
              ? errUsername
              : 'Changing your username may take a moment while we verify it stays unique.'
          }
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="First name"
            fullWidth
            required
            value={form.firstName}
            onChange={(e) => onFormChange({ firstName: e.target.value })}
            onBlur={() => onTouch('firstName')}
            error={touched.firstName && errFirst !== undefined}
            helperText={touched.firstName ? errFirst : undefined}
          />
          <TextField
            label="Last name"
            fullWidth
            required
            value={form.lastName}
            onChange={(e) => onFormChange({ lastName: e.target.value })}
            onBlur={() => onTouch('lastName')}
            error={touched.lastName && errLast !== undefined}
            helperText={touched.lastName ? errLast : undefined}
          />
        </Stack>
        <MuiTelInput
          label="Phone"
          value={form.phoneE164}
          onChange={(value) => onFormChange({ phoneE164: value })}
          onBlur={() => onTouch('phoneE164')}
          defaultCountry="US"
          preferredCountries={['US', 'CA', 'GB', 'AU']}
          forceCallingCode
          focusOnSelectCountry
          error={touched.phoneE164 && errPhone !== undefined}
          helperText={touched.phoneE164 ? errPhone : undefined}
        />
        <TextField
          label="Date of birth"
          type="date"
          value={form.dateOfBirth}
          onChange={(e) => onFormChange({ dateOfBirth: e.target.value })}
          onBlur={() => onTouch('dateOfBirth')}
          slotProps={{ inputLabel: { shrink: true } }}
          error={touched.dateOfBirth && errDob !== undefined}
          helperText={touched.dateOfBirth ? errDob : undefined}
        />
        <PlatformTileGrid
          label="Preferred platforms"
          value={form.preferredPlatforms}
          onChange={(next) => onFormChange({ preferredPlatforms: next })}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.optInNewsletter}
              onChange={(e) => onFormChange({ optInNewsletter: e.target.checked })}
            />
          }
          label="Subscribe to the DVGUP newsletter"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.optInPaidFeatures}
              onChange={(e) => onFormChange({ optInPaidFeatures: e.target.checked })}
            />
          }
          label="Interested in ad-free viewing / paid features"
        />
        {props.upsertStatus === 'failed' && (
          <Alert severity="error" role="alert">
            {props.upsertError ?? 'Save failed. Please try again.'}
          </Alert>
        )}
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save profile'}
        </Button>
      </Stack>
    </Box>
  );
}

/** Nested password-change form using the Cognito SDK. */
function ChangePasswordSection({ email }: { readonly email: string }): JSX.Element {
  const { userPoolId, userPoolClientId } = getCognitoConfig();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({ new: false, confirm: false });
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const errNew = passwordError(newPassword);
  const errConfirm = passwordConfirmError(newPassword, confirmPassword);

  async function handleSubmit(evt: FormEvent<HTMLFormElement>): Promise<void> {
    evt.preventDefault();
    setTouched({ new: true, confirm: true });
    if (oldPassword.length === 0 || errNew !== undefined || errConfirm !== undefined) return;
    setStatus('saving');
    setErrorMsg(undefined);
    try {
      await changePassword({
        userPoolId,
        userPoolClientId,
        email,
        oldPassword,
        newPassword,
      });
      setStatus('saved');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTouched({ new: false, confirm: false });
    } catch (err) {
      setStatus('failed');
      setErrorMsg(err instanceof Error ? err.message : 'password change failed');
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', mb: 2 }}>
        Change password
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Current password"
          type="password"
          required
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          autoComplete="current-password"
        />
        <Box>
          <TextField
            label="New password"
            type="password"
            required
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, new: true }))}
            autoComplete="new-password"
            error={touched.new && errNew !== undefined}
            helperText={touched.new && errNew !== undefined ? errNew : undefined}
          />
          {newPassword.length > 0 && <PasswordRulesChecklist value={newPassword} />}
        </Box>
        <TextField
          label="Confirm new password"
          type="password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, confirm: true }))}
          autoComplete="new-password"
          error={touched.confirm && errConfirm !== undefined}
          helperText={touched.confirm ? errConfirm : undefined}
        />
        {status === 'saved' && (
          <Alert severity="success" role="status">
            Password updated.
          </Alert>
        )}
        {status === 'failed' && (
          <Alert severity="error" role="alert">
            {errorMsg ?? 'Password change failed.'}
          </Alert>
        )}
        <Button
          type="submit"
          variant="outlined"
          disabled={status === 'saving'}
        >
          {status === 'saving' ? 'Updating…' : 'Update password'}
        </Button>
      </Stack>
    </Box>
  );
}
